import React from 'react';
import { Button, Modal, ModalContent, ModalBody, ModalFooter } from '@heroui/react';
import Trash from '@/icons/Trash';
import useIsMobile from '@/hooks/useIsMobile';
import { btnGhost } from '@/components/ui/design';

interface DeleteConfirmModalProps {
    isOpen: boolean;
    onClose: () => void;
    onConfirm: () => void;
    title: string;
    message: string;
    itemName?: string;
    loading?: boolean;
    confirmLabel?: string;
}

export default function DeleteConfirmModal({
    isOpen,
    onClose,
    onConfirm,
    title,
    message,
    itemName,
    loading = false,
    confirmLabel = 'Delete'
}: DeleteConfirmModalProps) {
    const isMobile = useIsMobile();

    return (
        <Modal
            isOpen={isOpen}
            onClose={onClose}
            size={isMobile ? 'full' : 'md'}
            isDismissable={!loading}
            classNames={{
                base: 'bg-white dark:bg-nightSurface rounded-none md:rounded-[28px] shadow-none md:ring-1 md:ring-black/[0.06] md:dark:ring-white/10 h-full md:h-auto',
                wrapper: 'p-0 md:p-4 z-[100000]',
                body: 'px-6',
                footer: 'px-6 pb-6',
                closeButton: 'right-4 top-4 rounded-full hover:bg-grey99 dark:hover:bg-white/10',
                backdrop: 'bg-black/50',
            }}
        >
            <ModalContent>
                <ModalBody className="pt-8 pb-4 flex flex-col justify-center md:justify-start">
                    <div className="flex items-center mb-4">
                        <div className="flex-shrink-0 w-12 h-12 bg-error95 dark:bg-error50/15 rounded-full flex items-center justify-center mr-4">
                            <Trash width={20} height={20} className="text-error50 dark:text-error60" />
                        </div>
                        <div>
                            <h3 className="text-2xl font-bold leading-tight tracking-[-0.03em] text-grey10 dark:text-white">{title}</h3>
                        </div>
                    </div>

                    <div>
                        <p className="text-base leading-relaxed text-grey40 dark:text-nightMuted mb-3">{message}</p>
                        {itemName && (
                            <div className="rounded-2xl bg-grey99 px-4 py-3 ring-1 ring-black/[0.04] dark:bg-white/[0.06] dark:ring-white/10">
                                <p className="text-sm font-semibold text-grey10 dark:text-white">{itemName}</p>
                            </div>
                        )}
                    </div>
                </ModalBody>

                <ModalFooter className="flex flex-col sm:flex-row gap-3">
                    <Button
                        onPress={onClose}
                        variant="bordered"
                        isDisabled={loading}
                        className={`${btnGhost} w-full sm:w-auto border-0 bg-transparent`}
                    >
                        Cancel
                    </Button>
                    <Button
                        onPress={onConfirm}
                        isLoading={loading}
                        className="inline-flex h-12 w-full sm:w-auto items-center justify-center gap-2 rounded-full bg-error50 px-6 text-sm font-semibold text-white transition-colors hover:bg-error40"
                    >
                        {confirmLabel}
                    </Button>
                </ModalFooter>
            </ModalContent>
        </Modal>
    );
}
